import { useMutation } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { initiateRegister, verifyOtp } from '../../api/apiOtp';

/**
 * Hook for two-step registration flow
 * Step 1: Initiate registration (send OTP)
 * Step 2: Verify OTP and complete registration
 * @returns {Object} Mutations and state for both steps
 */
export const useRegister = () => {
    const [registrationData, setRegistrationData] = useState(null);
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);

    // Step 1: Initiate registration
    const initiateMutation = useMutation({
        mutationFn: (userData) => {
            return initiateRegister(userData);
        },
        onSuccess: (data, variables) => {
            // Store registration data and OTP details for step 2
            setRegistrationData({
                channel: data?.data?.channel,
                identifier: data?.data?.identifier,
                purpose: data?.data?.purpose || 'register',
                expires_at: data?.data?.expires_at,
                cooldown_seconds: data?.data?.cooldown_seconds,
                // Keep original form data for potential re-submission
                formData: variables
            });
            setAttemptsRemaining(null); // Reset attempts for new OTP
        },
        onError: () => {
            setRegistrationData(null);
        }
    });

    // Step 2: Verify OTP and complete registration
    const verifyMutation = useMutation({
        mutationFn: ({ otp, cart_session_id }) => {
            if (!registrationData) {
                throw new Error('No registration data found. Please start registration again.');
            }

            return verifyOtp(
                registrationData.channel,
                registrationData.identifier,
                registrationData.purpose,
                otp,
                cart_session_id
            );
        },
        onSuccess: (data) => {
            // Registration complete - user is auto-logged in
            setAttemptsRemaining(null);
            // Clear registration data
            setRegistrationData(null);
        },
        onError: (error) => {
            // Track remaining attempts
            if (error?.response?.status === 422) {
                const remaining = error.response.data?.meta?.attempts_remaining;
                if (remaining !== undefined) {
                    setAttemptsRemaining(remaining);
                }
            }
        }
    });

    // Clear registration data
    const clearRegistrationData = useCallback(() => {
        setRegistrationData(null);
        setAttemptsRemaining(null);
    }, []);

    return {
        // Step 1
        initiate: initiateMutation.mutate,
        initiateAsync: initiateMutation.mutateAsync,
        isInitiating: initiateMutation.isPending,
        initiateError: initiateMutation.error,

        // Step 2
        verify: verifyMutation.mutate,
        verifyAsync: verifyMutation.mutateAsync,
        isVerifying: verifyMutation.isPending,
        verifyError: verifyMutation.error,

        // Shared state
        isPending: initiateMutation.isPending || verifyMutation.isPending,
        registrationData,
        setRegistrationData,
        attemptsRemaining,
        clearRegistrationData
    };
};
